import { z } from "zod";
import { normalizeFontFamilyName, toDisplayFontFamilyName } from "./typography.ts";

export const imageAssetSourceKindSchema = z.enum(["remote", "data_url", "inline_svg"]);

export const imageAssetMetaV1Schema = z.object({
  version: z.literal(1),
  kind: z.literal("image"),
});

export const imageAssetMetaV2Schema = z.object({
  version: z.literal(2),
  kind: z.literal("image"),
  sourceKind: imageAssetSourceKindSchema,
  isVector: z.boolean().default(false),
  width: z.number().int().positive().nullable().optional(),
  height: z.number().int().positive().nullable().optional(),
  pixelArea: z.number().int().positive().nullable().optional(),
  dhashHex: z
    .string()
    .regex(/^[0-9a-f]{16}$/)
    .nullable()
    .optional(),
  originalSourceUrlHash: z.string().length(64).optional(),
  sourceMimeType: z.string().min(1).optional(),
});

export const fontAssetFamilySchema = z.object({
  raw: z.string().min(1),
  normalized: z.string().min(1),
  display: z.string().min(1),
});

export const fontAssetMetaV1Schema = z.object({
  version: z.literal(1),
  kind: z.literal("font"),
  family: fontAssetFamilySchema.optional(),
  sourceFileName: z.string().min(1).optional(),
});

export const assetMetaV1Schema = z.union([
  imageAssetMetaV1Schema,
  imageAssetMetaV2Schema,
  fontAssetMetaV1Schema,
]);

export type ImageAssetMetaV1 = z.infer<typeof imageAssetMetaV1Schema>;
export type ImageAssetMetaV2 = z.infer<typeof imageAssetMetaV2Schema>;
export type FontAssetFamily = z.infer<typeof fontAssetFamilySchema>;
export type FontAssetMetaV1 = z.infer<typeof fontAssetMetaV1Schema>;
export type AssetMetaV1 = z.infer<typeof assetMetaV1Schema>;
export type ImageAssetMeta = ImageAssetMetaV1 | ImageAssetMetaV2;

export type AssetMetaJson = AssetMetaV1;

const toFontAssetFamily = (rawFamily: string): FontAssetFamily => ({
  raw: rawFamily,
  normalized: normalizeFontFamilyName(rawFamily),
  display: toDisplayFontFamilyName(rawFamily),
});

export const createImageAssetMetaV1 = (): ImageAssetMetaV1 => ({
  version: 1,
  kind: "image",
});

export interface CreateImageAssetMetaV2Input {
  sourceKind: z.infer<typeof imageAssetSourceKindSchema>;
  isVector: boolean;
  width?: number | null;
  height?: number | null;
  pixelArea?: number | null;
  dhashHex?: string | null;
  originalSourceUrlHash?: string | null;
  sourceMimeType?: string | null;
}

export const createImageAssetMetaV2 = (input: CreateImageAssetMetaV2Input): ImageAssetMetaV2 => ({
  version: 2,
  kind: "image",
  sourceKind: input.sourceKind,
  isVector: input.isVector,
  width: input.width ?? null,
  height: input.height ?? null,
  pixelArea: input.pixelArea ?? null,
  dhashHex: input.dhashHex ?? null,
  ...(input.originalSourceUrlHash ? { originalSourceUrlHash: input.originalSourceUrlHash } : {}),
  ...(input.sourceMimeType ? { sourceMimeType: input.sourceMimeType } : {}),
});

export const createFontAssetMetaV1 = (
  rawFamily: string | null | undefined,
  sourceFileName?: string | null,
): FontAssetMetaV1 => {
  const cleaned = typeof rawFamily === "string" ? rawFamily.trim() : "";
  const cleanedFileName = typeof sourceFileName === "string" ? sourceFileName.trim() : "";

  if (cleaned.length === 0) {
    return {
      version: 1,
      kind: "font",
      ...(cleanedFileName ? { sourceFileName: cleanedFileName } : {}),
    };
  }

  return {
    version: 1,
    kind: "font",
    family: toFontAssetFamily(cleaned),
    ...(cleanedFileName ? { sourceFileName: cleanedFileName } : {}),
  };
};

export const parseAssetMeta = (value: unknown, kindHint?: "image" | "font"): AssetMetaV1 | null => {
  const parsed = assetMetaV1Schema.safeParse(value);
  if (parsed.success) {
    return parsed.data;
  }

  if (!value || typeof value !== "object") {
    return kindHint === "image"
      ? createImageAssetMetaV2({ sourceKind: "remote", isVector: false })
      : kindHint === "font"
        ? createFontAssetMetaV1(null)
        : null;
  }

  const legacy = value as { fontFamily?: unknown };
  if (typeof legacy.fontFamily === "string" && legacy.fontFamily.trim().length > 0) {
    return createFontAssetMetaV1(legacy.fontFamily);
  }

  if (kindHint === "image") {
    return createImageAssetMetaV2({ sourceKind: "remote", isVector: false });
  }

  if (kindHint === "font") {
    return createFontAssetMetaV1(null);
  }

  return null;
};

export const getFontFamilyFromAssetMeta = (
  value: unknown,
  kindHint?: "image" | "font",
): FontAssetFamily | null => {
  const meta = parseAssetMeta(value, kindHint);
  if (!meta || meta.kind !== "font") {
    return null;
  }

  return meta.family ?? null;
};
